import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, Calendar, User, Car, Eye, AlertCircle, CheckCircle, Clock, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import api from '@/api/api';
import { toast } from 'sonner';

export default function ComplianceList({ entityFilter = null }) {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const res = await api.get('/documents/alerts');
        setAlerts(res.data?.data || []);
      } catch (err) {
        toast.error('Erro ao carregar alertas');
      } finally {
        setLoading(false);
      }
    };
    fetchAlerts();
  }, []);

  const visibleAlerts = entityFilter
    ? alerts.filter((a) => a.entity_type === entityFilter)
    : alerts;

  const getStatusInfo = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { label: 'Expirado', color: 'text-red-600 bg-red-50 border-red-100', icon: AlertCircle };
    if (diffDays <= 15) return { label: `Expira em ${diffDays} dias`, color: 'text-amber-600 bg-amber-50 border-amber-100', icon: Clock };
    return { label: 'Pendente', color: 'text-blue-600 bg-blue-50 border-blue-100', icon: Info };
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Central de Conformidade</h1>
        <p className="text-sm text-muted-foreground mt-1">Monitorização de Validades e Seguros</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ShieldAlert className="text-red-500" />
            Alertas Ativos ({visibleAlerts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Entidade</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Documento</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Validade</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Estado</th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground uppercase">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="p-12 text-center text-muted-foreground">A analisar arquivos...</td>
                  </tr>
                ) : visibleAlerts.length > 0 ? (
                  visibleAlerts.map((alert) => {
                    const status = getStatusInfo(alert.expiry_date);
                    return (
                      <tr key={alert.id} className="hover:bg-muted/50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
                              {alert.entity_type === 'driver' ? <User size={16} /> : <Car size={16} />}
                            </div>
                            <div>
                              <div className="font-semibold text-sm">{alert.owner_name}</div>
                              <div className="text-xs text-muted-foreground uppercase">{alert.entity_type}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">{alert.type_name}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Calendar size={14} />
                            {new Date(alert.expiry_date).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase border ${status.color}`}>
                            {status.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => navigate(`/${alert.entity_type}s/docs/${alert.entity_id}`)}
                          >
                            <Eye size={18} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="p-12 text-center">
                      <CheckCircle size={48} className="mx-auto text-emerald-200 mb-4" />
                      <p className="text-xs font-semibold uppercase text-muted-foreground">Frota 100% Conforme</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
